import { createContext, useCallback, useReducer } from 'react';
import type { FC } from 'react';
import { vendorBoothReducer } from './VendorBoothContextFunctions';
import { ContextProviderProps } from 'src/types/contextProviderProps';
import { BoothContact, initialProductHighlight, initialVendorBooth, ProductHighlight, VendorBooth } from 'src/types/vendorBooth';
import UmbarcoApi from 'src/api/UmbarcoApi';
import ItemApi from 'src/api/ItemApi';
import { State } from '@progress/kendo-data-query';

export interface IVendorBoothContext {
  vendorBooth: VendorBooth;
  loadBooth: (boothId: number, vendorNumber: number) => void;
  saveVendorBooth: (vendor: VendorBooth) => void;
  updateProperty: (property: string, value: string | number) => void;

  updateProductHighlight: (formData: ProductHighlight, index: number) => void;
  addNewProductHighlight: () => void;
  deleteProductHighlight: (index: number) => void;

  deleteMedia: (udi: string, alias: string, boothId: number) => void;

  updateContact: (formData: BoothContact, index: number) => void;
  addNewContact: () => void;
  deleteContact: (index: number) => void;
  resetBoothToDefault: () => void;
}

const VendorBoothContext = createContext<IVendorBoothContext>({
  vendorBooth: { ...initialVendorBooth },
  loadBooth: () => {},
  saveVendorBooth: () => {},
  updateProperty: () => {},
  updateProductHighlight: () => {},
  addNewProductHighlight: () => {},
  deleteProductHighlight: () => {},
  deleteMedia: () => {},

  updateContact: () => {},
  addNewContact: () => {},
  deleteContact: () => {},
  resetBoothToDefault: () => {},
});

export const VendorBoothProvider: FC<ContextProviderProps> = ({ children }) => {
  const [vendorBooth, dispatch] = useReducer(vendorBoothReducer, initialVendorBooth);

  const resetBoothToDefault = useCallback(() => {
    dispatch({
      type: 'SetVendorBooth',
      payload: { ...initialVendorBooth },
    });
  }, []);

  const loadBooth = useCallback(async (boothId: number, vendorNumber: number) => {
    dispatch({
      type: 'SetVendorBooth',
      payload: { ...initialVendorBooth, isLoading: true },
    });

    if (boothId === undefined || boothId === 0) return;

    let booth: VendorBooth = await UmbarcoApi.getBoothById(boothId);

    let si = await ItemApi.getItems(
      {
        take: 200,
        filter: {
          logic: 'and',
          filters: [
            {
              field: 'VendorNumber',
              operator: 'eq',
              value: vendorNumber,
            },
          ],
        },
      } as State,
      ''
    );

    booth.startingItems = si.value;

    booth.isLoading = false;
    dispatch({
      type: 'SetVendorBooth',
      payload: booth,
    });
  }, []);

  const saveVendorBooth = useCallback((updatedVendorBooth: VendorBooth): void => {
    dispatch({
      type: 'SetVendorBooth',
      payload: updatedVendorBooth,
    });
  }, []);

  const updateProperty = useCallback(
    async (property: string, value: string | number) => {
      // Check if other property value has changed
      let prop = vendorBooth.boothProperties.find((x) => x.alias === property);
      if (prop && prop.value === value) {
        return;
      }

      //If prop is boothName strip out any periods
      if (property === 'boothName') {
        value = value.toString().replaceAll('.', '');
        value = value.toString().replaceAll('&', 'and');
      }

      if (property === 'mainBoothVideoUrl') {
        // If value starts with https://youtu.be/ OR https://www.youtube.com/watch?v= Then replace with https://youtube.com/embed/
        // Also remove the & channel part if it is there
        let strValue = value.toString();
        if (strValue.startsWith('https://youtu.be/')) {
          const videoId = strValue.split('https://youtu.be/')[1].split('&')[0];
          value = 'https://youtube.com/embed/' + videoId;
        } else if (strValue.startsWith('https://www.youtube.com/watch?v=')) {
          const videoId = strValue.split('https://www.youtube.com/watch?v=')[1].split('&')[0];
          value = 'https://youtube.com/embed/' + videoId;
        }
      }

      if (prop) {
        // Only dispatch for properties that exist
        dispatch({
          type: 'UpdateProperty',
          payload: { property: property, value: value },
        });
      }

      let success: boolean = await UmbarcoApi.updateProperty(vendorBooth.id, property, value);
      console.log('updateProperty Success: ', success);
    },
    [vendorBooth.id, vendorBooth.boothProperties, vendorBooth.name] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const deleteMedia = useCallback(async (udi: string, alias: string, boothId: number) => {
    let response = await UmbarcoApi.deleteMediaByUdi(udi, alias, boothId);
    if (response.success === true) {
      dispatch({
        type: 'UpdateProperty',
        payload: { property: alias, value: null },
      });
    } else {
      console.error('deleteMedia Error: ', response.errors.join(', '));
    }
  }, []);

  //#region - Product Highlights
  const updateProductHighlight = useCallback(
    async (formData: ProductHighlight, index: number) => {
      let items = [...vendorBooth.productHighlights];

      //Only save the item number to Umbraco, all items are looked up in the PIM, the other fields don't matter.
      items[index] = { ...initialProductHighlight, key: formData.key, itemNumber: formData.itemNumber };

      dispatch({
        type: 'SetProductHighlights',
        payload: [...items],
      });

      let success = await UmbarcoApi.saveProductHighlights(vendorBooth.id, items);
      console.log('updateProductHighlight Success: ', success);
    },
    [vendorBooth.id, vendorBooth.productHighlights]
  );

  const addNewProductHighlight = useCallback(async () => {
    let items = [...(vendorBooth.productHighlights ?? [])];
    // close all open items
    items.forEach((item) => {
      item.inEdit = false;
    });

    items.push({ ...initialProductHighlight, inEdit: true });
    dispatch({
      type: 'SetProductHighlights',
      payload: [...items],
    });
  }, [vendorBooth.productHighlights]);

  const deleteProductHighlight = useCallback(
    async (index: number) => {
      let items = [...vendorBooth.productHighlights];
      items.splice(index, 1);
      dispatch({
        type: 'SetProductHighlights',
        payload: [...items],
      });

      let success = await UmbarcoApi.saveProductHighlights(vendorBooth.id, items);
      console.log('deleteProductHighlight Success: ', success);
    },
    [vendorBooth.id, vendorBooth.productHighlights]
  );

  //#endregion

  //#region - Booth Contacts
  const updateContact = useCallback(
    async (formData: BoothContact, index: number) => {
      let items = [...vendorBooth.boothContacts];
      items[index] = { ...formData };

      dispatch({
        type: 'SetContacts',
        payload: [...items],
      });

      let success = await UmbarcoApi.saveContacts(vendorBooth.id, items);
      console.log('updateContact Success: ', success);
    },
    [vendorBooth.boothContacts, vendorBooth.id]
  );

  const addNewContact = useCallback(async () => {
    let items = [...(vendorBooth?.boothContacts ?? [])];
    // close all open items
    items.forEach((item) => {
      item.inEdit = false;
    });

    items.push({ fullName: '', emailAddress: '', phoneNumber: '', inEdit: true });
    dispatch({
      type: 'SetContacts',
      payload: [...items],
    });
  }, [vendorBooth.boothContacts, vendorBooth.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteContact = useCallback(
    async (index: number) => {
      let items = [...vendorBooth.boothContacts];
      items.splice(index, 1);
      dispatch({
        type: 'SetContacts',
        payload: [...items],
      });

      let success = await UmbarcoApi.saveContacts(vendorBooth.id, items);
      console.log('deleteContact Success: ', success);
    },
    [vendorBooth.boothContacts, vendorBooth.id]
  );

  //#endregion

  return (
    <VendorBoothContext.Provider
      value={{
        vendorBooth,
        loadBooth,
        saveVendorBooth,
        updateProperty,
        updateProductHighlight,
        addNewProductHighlight,
        deleteProductHighlight,
        deleteMedia,
        updateContact,
        addNewContact,
        deleteContact,
        resetBoothToDefault,
      }}
    >
      {children}
    </VendorBoothContext.Provider>
  );
};

export default VendorBoothContext;
